import { useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import type { ITestSuite } from '../../interfaces/ui';
import '../../styles/executionCenter.css';

interface TestCasesTabProps {
  testSuites: ITestSuite[];
  loading: boolean;
  error: string | null;
}

export default function TestCasesTab({
  testSuites,
  loading,
  error,
}: TestCasesTabProps) {
  const theme = useTheme();
  const [expandedSuiteNames, setExpandedSuiteNames] = useState<string[]>([]);

  const toggleSuite = (suiteName: string) => {
    setExpandedSuiteNames(current =>
      current.includes(suiteName)
        ? current.filter(name => name !== suiteName)
        : [...current, suiteName],
    );
  };

  if (loading) {
    return (
      <Box className="ec-tab-loading">
        <CircularProgress size={40} color="primary" />
        <Typography>Loading Test Cases...</Typography>
      </Box>
    );
  }

  if (!testSuites || testSuites.length === 0) {
    return (
      <Box className="ec-results-pending">
        <Typography component="p">Test Cases are being generated...</Typography>
      </Box>
    );
  }
  if (error) return <Typography className="ec-tab-error">{error}</Typography>;

  return (
    <Box className="ec-tab-panel">
      <Typography className="ec-artifact-title">Test Scenario</Typography>
      <Box className="ec-test-suites-list">
        {testSuites.length === 0 && (
          <Typography className="ec-empty-suite-message">
            No test suites available for this run.
          </Typography>
        )}
        {testSuites.map(suite => {
          const isExpanded = expandedSuiteNames.includes(suite.name);
          return (
            <Box key={suite.name} className="ec-test-suite">
              <Button
                color="primary"
                className="ec-test-suite-toggle"
                onClick={() => toggleSuite(suite.name)}
                aria-expanded={isExpanded}
                startIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              >
                <Box className="ec-test-suite-header-content">
                  <Typography className="ec-test-suite-title">
                    {suite.name}
                  </Typography>
                  <Box
                    component="span"
                    className="ec-test-suite-count-badge"
                    style={{
                      backgroundColor: theme.palette.action.hover,
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.secondary,
                    }}
                  >
                    {`${suite.tests.length} tests`}
                  </Box>
                </Box>
              </Button>
              {isExpanded && (
                <Box className="ec-test-suite-steps">
                  {suite.tests.length === 0 ? (
                    <Typography className="ec-empty-suite-message">
                      This suite has no tests.
                    </Typography>
                  ) : (
                    suite.tests.map((test, testIndex) => (
                      <Box
                        key={`${suite.name}-${test.name}-${testIndex}`}
                        className="ec-test-case"
                        style={{
                          borderLeftColor: theme.palette.primary.main,
                        }}
                      >
                        <Typography
                          className="ec-test-case-title"
                          component="div"
                        >
                          <Box
                            component="span"
                            className="ec-test-case-number-badge"
                            style={{
                              backgroundColor: theme.palette.primary.main,
                              color: theme.palette.primary.contrastText,
                            }}
                          >
                            {testIndex + 1}
                          </Box>
                          <Box component="span">{test.title || test.name}</Box>
                        </Typography>

                        {test.preconditions.length > 0 && (
                          <>
                            <Typography
                              className="ec-test-case-section-title"
                              style={{ color: theme.palette.primary.main }}
                            >
                              Preconditions:
                            </Typography>
                            <Box
                              component="ul"
                              className="ec-test-list ec-test-list-tight"
                            >
                              {test.preconditions.map(
                                (precondition, preIndex) => (
                                  <Typography
                                    key={`${suite.name}-${test.name}-pre-${preIndex}`}
                                    component="li"
                                    className="ec-test-list-item ec-test-list-item-tight"
                                  >
                                    {precondition}
                                  </Typography>
                                ),
                              )}
                            </Box>
                          </>
                        )}

                        <Typography
                          className="ec-test-case-section-title"
                          style={{ color: theme.palette.primary.main }}
                        >
                          Test Steps:
                        </Typography>
                        {test.steps.length === 0 ? (
                          <Typography className="ec-empty-suite-message">
                            This test has no steps.
                          </Typography>
                        ) : (
                          <Box
                            component="ol"
                            className="ec-test-list ec-test-list-ordered ec-test-list-tight"
                          >
                            {test.steps.map((step, stepIndex) => (
                              <Typography
                                key={`${suite.name}-${test.name}-step-${stepIndex}`}
                                component="li"
                                className="ec-test-list-item ec-test-list-item-tight"
                              >
                                {step.instruction}
                              </Typography>
                            ))}
                          </Box>
                        )}

                        {test.steps.some(
                          step => step.expectedResults.length > 0,
                        ) && (
                          <>
                            <Typography
                              className="ec-test-case-section-title"
                              style={{ color: theme.palette.primary.main }}
                            >
                              Expected Results:
                            </Typography>
                            <Box
                              component="ul"
                              className="ec-test-list ec-test-list-tight"
                            >
                              {test.steps.flatMap((step, stepIndex) =>
                                step.expectedResults.map(
                                  (expectedResult, expectedIndex) => (
                                    <Typography
                                      key={`${suite.name}-${test.name}-${stepIndex}-expected-${expectedIndex}`}
                                      component="li"
                                      className="ec-test-list-item ec-test-list-item-tight"
                                    >
                                      {expectedResult}
                                    </Typography>
                                  ),
                                ),
                              )}
                            </Box>
                          </>
                        )}
                      </Box>
                    ))
                  )}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
